/**
 * Iraqi Phone Number Validator and Formatter
 * Handles validation and formatting for Iraqi mobile numbers
 * Supports both local (07XX XXX XXXX) and international (+964 7XX XXX XXXX) formats
 */
class IraqiPhoneValidator {
    /**
     * Format Iraqi mobile phone number
     * @param {string} value - The input phone number value
     * @returns {string} - Formatted phone number or empty string if invalid
     */
    static formatPhoneNumber(value) {
        // Remove any invalid characters except digits and single + at start
        let cleanValue = value;
        
        // Only allow + at the beginning
        if (cleanValue.includes('+')) {
            const firstPlusIndex = cleanValue.indexOf('+');
            if (firstPlusIndex === 0) {
                // Keep only the first + and remove any others
                cleanValue = '+' + cleanValue.slice(1).replace(/\+/g, '');
            } else {
                // Remove all + symbols if not at start
                cleanValue = cleanValue.replace(/\+/g, '');
            }
        }
        
        // Handle +964 prefix
        if (cleanValue.startsWith('+')) {
            return this._formatInternationalNumber(cleanValue);
        }
        
        // Handle local format
        return this._formatLocalNumber(cleanValue);
    }

    /**
     * Format international number (+964 format)
     * @private
     */
    static _formatInternationalNumber(cleanValue) {
        // Extract digits after +
        const digitsAfterPlus = cleanValue.slice(1).replace(/\D/g, '');
        
        // Validate progressive typing of +964
        if (digitsAfterPlus.length === 0) return '+';
        if (digitsAfterPlus.length === 1 && digitsAfterPlus === '9') return '+9';
        if (digitsAfterPlus.length === 2 && digitsAfterPlus === '96') return '+96';
        if (digitsAfterPlus.length === 3 && digitsAfterPlus === '964') return '+964';
        
        // Must start with 964 for Iraqi numbers
        if (!digitsAfterPlus.startsWith('964')) {
            // If not starting with 964, only allow up to 964
            if (digitsAfterPlus.length <= 3) {
                return '+' + digitsAfterPlus;
            } else {
                return '+964';
            }
        }
        
        // Handle complete +964 format
        if (digitsAfterPlus.startsWith('964')) {
            const mobileDigits = digitsAfterPlus.slice(3); // Remove 964
            
            if (mobileDigits.length === 0) return '+964';
            
            // Must start with 7 for mobile (reject 0, 1, 2, 3, 4, 5, 6, 8, 9)
            if (mobileDigits.startsWith('7')) {
                if (mobileDigits.length <= 3) {
                    return `+964 ${mobileDigits}`;
                } else if (mobileDigits.length <= 6) {
                    return `+964 ${mobileDigits.slice(0, 3)} ${mobileDigits.slice(3)}`;
                } else if (mobileDigits.length <= 10) {
                    return `+964 ${mobileDigits.slice(0, 3)} ${mobileDigits.slice(3, 6)} ${mobileDigits.slice(6, 10)}`;
                } else {
                    // Limit to 10 digits after 964
                    return `+964 ${mobileDigits.slice(0, 3)} ${mobileDigits.slice(3, 6)} ${mobileDigits.slice(6, 10)}`;
                }
            } else {
                // Invalid mobile number after +964 - only allow 7XX
                return '+964';
            }
        }
    }

    /**
     * Format local number (07XX format)
     * @private
     */
    static _formatLocalNumber(cleanValue) {
        // Remove all non-digit characters for local format
        const digitsOnly = cleanValue.replace(/\D/g, '');
        
        // Iraqi mobile number pattern: 07XX XXX XXXX (11 digits total)
        if (digitsOnly.length === 0) return '';
        
        // Handle mobile numbers (07XX XXX XXXX) - only allow 07XX format
        if (digitsOnly.startsWith('07')) {
            if (digitsOnly.length <= 4) {
                return digitsOnly;
            } else if (digitsOnly.length <= 7) {
                return `${digitsOnly.slice(0, 4)} ${digitsOnly.slice(4)}`;
            } else if (digitsOnly.length <= 11) {
                return `${digitsOnly.slice(0, 4)} ${digitsOnly.slice(4, 7)} ${digitsOnly.slice(7, 11)}`;
            } else {
                // Limit to 11 digits for mobile
                return `${digitsOnly.slice(0, 4)} ${digitsOnly.slice(4, 7)} ${digitsOnly.slice(7, 11)}`;
            }
        }
        // For numbers that don't start with 07, handle progressive typing
        else {
            // Allow single '0' for progressive typing
            if (digitsOnly === '0') {
                return '0';
            }
            // Only allow numbers that start with 0 if they are 07XX
            if (digitsOnly.startsWith('0') && !digitsOnly.startsWith('07')) {
                return '0'; // Reset to '0' for invalid formats like 00XX, 01XX, etc.
            }
            // Reject all other invalid formats
            return '';
        }
    }

    /**
     * Validate if a phone number is complete and valid
     * @param {string} phoneNumber - The phone number to validate
     * @returns {boolean} - True if valid, false otherwise
     */
    static isValidPhoneNumber(phoneNumber) {
        if (!phoneNumber) return false;
        
        const cleanNumber = phoneNumber.replace(/\s/g, ''); // Remove spaces
        
        // Check international format: +964 7XX XXX XXXX (14 digits with +964)
        if (cleanNumber.startsWith('+964')) {
            const digits = cleanNumber.slice(4);
            return digits.length === 10 && digits.startsWith('7') && /^\d{10}$/.test(digits);
        }
        
        // Check local format: 07XX XXX XXXX (11 digits)
        if (cleanNumber.startsWith('07')) {
            return cleanNumber.length === 11 && /^07\d{9}$/.test(cleanNumber);
        }
        
        return false;
    }

    /**
     * Get placeholder text for input field
     * @returns {string} - Placeholder text
     */
    static getPlaceholder() {
        return '+964 7XX XXX XXXX or 07XX XXX XXXX';
    }

    /**
     * Get help text for input field
     * @returns {string} - Help text
     */
    static getHelpText() {
        return 'Enter Iraqi mobile number (+964 7XX XXX XXXX or 07XX XXX XXXX)';
    }
}

export default IraqiPhoneValidator;
